import React from 'react';
import '../../../../../styles/components/auction-details.scss';

/* ------------------------------------------------------------------ */
/*  Single flip card                                                   */
/* ------------------------------------------------------------------ */
class FlipCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      display: props.value,
      prev: props.value,
      flipping: false,
    };
    this.flipTimeout = null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      if (this.flipTimeout) clearTimeout(this.flipTimeout);
      this.setState({
        prev: this.state.display,
        flipping: true,
      });
      this.flipTimeout = setTimeout(() => {
        this.setState({
          display: this.props.value,
          flipping: false,
        });
      }, 400);
    }
  }

  componentWillUnmount() {
    if (this.flipTimeout) clearTimeout(this.flipTimeout);
  }

  render() {
    const { label } = this.props;
    const { display, prev, flipping } = this.state;

    const flipTop = flipping ? prev : display;
    const flipBottom = flipping ? this.props.value : display;

    return (
      <div className="fliqlo-card-wrapper">
        <div className="fliqlo-card">
          {/* static top half */}
          <div className="fliqlo-half fliqlo-half--top">
            <span className="fliqlo-digit fliqlo-digit--top">{display}</span>
          </div>

          {/* static bottom half */}
          <div className="fliqlo-half fliqlo-half--bottom">
            <span className="fliqlo-digit fliqlo-digit--bottom">{display}</span>
          </div>

          {/* flip top (folds down) */}
          <div
            className={
              'fliqlo-half fliqlo-half--top fliqlo-flip-top' +
              (flipping ? ' fliqlo-flip-top--active' : '')
            }
          >
            <span className="fliqlo-digit fliqlo-digit--top">{flipTop}</span>
          </div>

          {/* flip bottom (unfolds) */}
          <div
            className={
              'fliqlo-half fliqlo-half--bottom fliqlo-flip-bottom' +
              (flipping ? ' fliqlo-flip-bottom--active' : '')
            }
          >
            <span className="fliqlo-digit fliqlo-digit--bottom">{flipBottom}</span>
          </div>

          {/* centre line */}
          <div className="fliqlo-centre-line" />
        </div>
        <span className="fliqlo-label">{label}</span>
      </div>
    );
  }
}

/* ------------------------------------------------------------------ */
/*  Separator                                                          */
/* ------------------------------------------------------------------ */
function Separator() {
  return (
    <div className="fliqlo-separator">
      <div className="fliqlo-dot" />
      <div className="fliqlo-dot" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Pad helper                                                         */
/* ------------------------------------------------------------------ */
const pad = (n) => String(n).padStart(2, '0');

/* ------------------------------------------------------------------ */
/*  Main countdown component                                           */
/* ------------------------------------------------------------------ */
/*
  Props:
    targetDate      — end date (ISO string or Date object)
    nowFn           — optional function returning current time (e.g. () => new Date(auction.server_time))
    onComplete      — callback when countdown reaches zero
    finishedText    — text to display when done (default: "TIME IS UP")
    labelDays       — custom label (default: "DAYS")
    labelHours      — custom label (default: "HOURS")
    labelMinutes    — custom label (default: "MINUTES")
    labelSeconds    — custom label (default: "SECONDS")
*/
export class FlipCountdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.calcRemaining();
    this.intervalId = null;
  }

  calcRemaining() {
    const target = this.props.targetDate
      ? new Date(String(this.props.targetDate).replace(/-/g, '/')).getTime()
      : Date.now() + 3 * 24 * 60 * 60 * 1000;

    // Use local monotonic clock + fixed offset — no dependency on parent's interval
    const now = Date.now() + (this.serverOffset || 0);
    const diff = Math.max(0, target - now);

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      done: diff === 0,
    };
  }

  componentDidMount() {
    // Snapshot the offset between server clock and local clock — once
    const serverNow = this.props.nowFn ? this.props.nowFn().getTime() : Date.now();
    this.serverOffset = serverNow - Date.now();

    this.intervalId = setInterval(() => {
      const remaining = this.calcRemaining();
      this.setState(remaining);
      if (remaining.done) {
        if (this.props.onComplete) this.props.onComplete();
        clearInterval(this.intervalId);
      }
    }, 1000);
  }

  componentDidUpdate(prevProps) {
    // Recalculate offset if server time source changes (e.g. extra time added)
    if (prevProps.targetDate !== this.props.targetDate) {
      if (this.props.nowFn) {
        this.serverOffset = this.props.nowFn().getTime() - Date.now();
      }
      this.setState(this.calcRemaining());
    }
  }

  componentWillUnmount() {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  render() {
    const { days, hours, minutes, seconds, done } = this.state;
    const {
      labelDays = 'DAYS',
      labelHours = 'HOURS',
      labelMinutes = 'MINUTES',
      labelSeconds = 'SECONDS',
    } = this.props;

    if (done) {
      return (
        <div className="fliqlo-root">
          <div className="fliqlo-finished">
            {this.props.finishedText || 'TIME IS UP'}
          </div>
        </div>
      );
    }

    return (
      <div className="fliqlo-root">
        <div className="fliqlo-glow" />
        <div className="fliqlo-row">
          <FlipCard value={pad(days)} label={labelDays} />
          <Separator />
          <FlipCard value={pad(hours)} label={labelHours} />
          <Separator />
          <FlipCard value={pad(minutes)} label={labelMinutes} />
          <Separator />
          <FlipCard value={pad(seconds)} label={labelSeconds} />
        </div>
      </div>
    );
  }
}
